import { useEffect, useRef } from 'react';
import { Auth } from 'aws-amplify';
import { isNetworkError, isOnline, subscribeConnectivity } from '../services/connectivity';

// Re-validate a beat after the ID token's stated expiry, so we don't fire
// while it's technically still valid.
const EXPIRY_BUFFER_MS = 2_000;
// Floor — a near-expired token mustn't busy-loop the timer.
const MIN_TIMER_MS = 3_000;
// Ceiling — keeps setTimeout delays sane and forces a periodic re-check even
// if a token reports an unexpectedly distant expiry.
const MAX_TIMER_MS = 60 * 60 * 1_000;

/**
 * Proactively detects Cognito session expiry and invokes `onExpired` so the
 * app can sign out and bounce to the login screen — without waiting for the
 * user to make an API request.
 *
 * Two triggers:
 *   • A timer armed to the ID token's expiry. When it fires we call
 *     `Auth.currentSession()`, which transparently refreshes the ID token
 *     using the refresh token. If that succeeds the session is still alive and
 *     we re-arm to the new expiry; if it throws the refresh token itself is
 *     dead — a genuine expiry — and we sign out.
 *   • Window focus / tab visibility — catches the common "left open overnight,
 *     refresh token lapsed while the tab was hidden" case the instant the user
 *     returns, rather than on the next timer tick.
 *
 * `enabled` gates the whole guard (pass false on the auth screens / when no
 * user is signed in). `onExpired` is held in a ref so callers can pass an
 * inline function without re-running the effect.
 */
export function useSessionExpiryGuard(
  enabled: boolean,
  onExpired: (reason: string) => void | Promise<void>,
) {
  const onExpiredRef = useRef(onExpired);
  onExpiredRef.current = onExpired;

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    // Prevents the timer and a focus/visibility check from validating (and
    // potentially signing out) concurrently.
    let handling = false;

    const clearTimer = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const armTimer = (expMs: number) => {
      clearTimer();
      const delay = Math.min(
        MAX_TIMER_MS,
        Math.max(MIN_TIMER_MS, expMs - Date.now() + EXPIRY_BUFFER_MS),
      );
      timer = setTimeout(() => { void validate(); }, delay);
    };

    const validate = async () => {
      if (cancelled || handling) return;
      handling = true;
      try {
        // Auto-refreshes the ID token if it's expired but the refresh token is
        // still valid. Throws only when the session genuinely can't be sustained.
        const session = await Auth.currentSession();
        if (cancelled) return;
        // getExpiration() is seconds since the epoch.
        armTimer(session.getIdToken().getExpiration() * 1_000);
      } catch (err) {
        if (cancelled) return;
        clearTimer();
        // Offline, the refresh fails for want of a network, not because the
        // session died — signing out here would throw an offline user out
        // within the hour. Stand down; the reconnect below re-validates.
        if (isNetworkError(err) || !isOnline()) return;
        await onExpiredRef.current('Your session has expired. Please sign in again.');
      } finally {
        handling = false;
      }
    };

    const onVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') void validate();
    };

    // Back from offline: the timer may have stood down while we were away, so
    // re-validate (and re-arm) as soon as the API answers again.
    let wasOnline = isOnline();
    const onConnectivity = () => {
      const online = isOnline();
      if (online && !wasOnline) void validate();
      wasOnline = online;
    };

    // Initial arm, then watch for the tab regaining focus/visibility.
    void validate();
    window.addEventListener('focus', onVisibilityOrFocus);
    document.addEventListener('visibilitychange', onVisibilityOrFocus);
    const unsubscribeConnectivity = subscribeConnectivity(onConnectivity);

    return () => {
      cancelled = true;
      clearTimer();
      unsubscribeConnectivity();
      window.removeEventListener('focus', onVisibilityOrFocus);
      document.removeEventListener('visibilitychange', onVisibilityOrFocus);
    };
  }, [enabled]);
}
